/**
 * SwitcherPopover — appears when the sidebar's switcher pill is clicked.
 *
 * Layout (per spec §5.3):
 *   [⊙ All Clients (Portfolio Mode)]   ← visually separated, first
 *   ─────────────────────────────────
 *   Recent (≤3, ordered by last access)
 *   · Client A
 *   · Client B
 *   · Client C
 *   ─────────────────────────────────
 *   → View all clients (link to /clients)
 *
 * Pinned section is a future iteration (per spec §6 — out of scope here).
 */

import clsx from "clsx";
import { Link } from "react-router-dom";

import type { Client } from "@/services/clientService";
import { getClientSlug } from "@/types/client";

interface SwitcherPopoverProps {
  recents: Client[];
  currentMode: "portfolio" | "scoped";
  currentClientId: string | null;
  onSelectAllClients: () => void;
  onSelectClient: (slug: string) => void;
}

export function SwitcherPopover({
  recents,
  currentMode,
  currentClientId,
  onSelectAllClients,
  onSelectClient,
}: SwitcherPopoverProps) {
  const recentsToShow = recents.slice(0, 3);

  return (
    <div role="menu" className="flex flex-col gap-1 p-2 min-w-56">
      <button
        type="button"
        role="menuitem"
        aria-current={currentMode === "portfolio" ? "true" : undefined}
        onClick={onSelectAllClients}
        className={clsx(
          "flex items-center gap-2 px-2 py-2 rounded text-left text-sm",
          "text-[var(--ws-text)] hover:bg-[var(--ws-accent-soft)]",
          currentMode === "portfolio" &&
            "bg-[var(--ws-accent-soft)] font-medium",
        )}
        aria-label="All Clients"
      >
        <span aria-hidden="true">⊙</span>
        <span>All Clients</span>
      </button>

      {recentsToShow.length > 0 && (
        <>
          <div className="border-t border-[var(--ws-border-soft)] my-1" />
          <div className="px-2 py-1 text-xs uppercase tracking-wide text-[var(--ws-text-tertiary)]">
            Recent
          </div>
          {recentsToShow.map((c) => (
            <button
              key={c.id}
              type="button"
              role="menuitem"
              aria-current={
                currentMode === "scoped" && currentClientId === c.id
                  ? "true"
                  : undefined
              }
              onClick={() => onSelectClient(getClientSlug(c))}
              className={clsx(
                "flex items-center gap-2 px-2 py-2 rounded text-left text-sm",
                "text-[var(--ws-text)] hover:bg-[var(--ws-accent-soft)]",
                currentMode === "scoped" &&
                  currentClientId === c.id &&
                  "bg-[var(--ws-accent-soft)] font-medium",
              )}
            >
              <span>{c.name}</span>
            </button>
          ))}
        </>
      )}

      <div className="border-t border-[var(--ws-border-soft)] my-1" />
      <Link
        to="/clients"
        role="menuitem"
        className="flex items-center gap-2 px-2 py-2 rounded text-sm text-[var(--ws-text)] hover:bg-[var(--ws-accent-soft)]"
      >
        <span aria-hidden="true">→</span>
        <span>View all clients</span>
      </Link>
    </div>
  );
}

export default SwitcherPopover;
